import React, { useEffect, useState } from "react";
import { InputAdornment } from "@mui/material";
import {
	CustomButton,
	CustomIconButton,
	CustomTextField,
	icon,
	IconComponent,
} from "../../../../../../../../../common/components";
import { translate } from "../../../../../../../../../common/providers";
import styles from "./ViewCreationToolbox.module.css";
import { debounce, isNonEmptyArray } from "../../../../../../../../../common/utils";
import { CRITERIA_TYPES, SECTION } from "../../../../../constants";
import CriteriaCreation from "../../../../criteria-creation/CriteriaCreation";
import { useApi } from "../../../../../../../../../common/hooks";
import { FormStudioService } from "../../../../../../../../../api";

const debouncedFunction = debounce((func) => func());

export default function ViewCreationToolbox({ usedFields = [], onAdd }) {
	const [criteriaList, setCriteriaList] = useState([]);
	const [searchValue, setSearchValue] = useState("");
	const [dialogType, setDialogType] = useState("");
	const [searchValueDebounced, setSearchValueDebounced] = useState("");
	const [count, setCount] = useState(0);
	const { call: getAllCriteria } = useApi(FormStudioService.getAllCriteria);
	useEffect(() => {
		getAllCriteria({ name: searchValueDebounced })
			.then((data) => {
				setCriteriaList(data);
			})
			.catch(console.error);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchValueDebounced, count]);
	const handleResetSearchText = () => {
		setSearchValue("");
		setSearchValueDebounced("");
	};
	const handleChangeSearchText = (event) => {
		const { value } = event.target;
		setSearchValue(value);
		debouncedFunction(() => {
			setSearchValueDebounced(value);
		});
	};
	const handleDragStart = (event, newItem) => {
		event.dataTransfer.setData("text/plain", null); // Necessary for Firefox
		event.currentTarget.classList.add(styles.dragging);
		let newItemType = newItem;
		if (newItem === SECTION.SECTION || newItem === SECTION.SUBSECTION) {
			newItemType = {
				id: String(new Date().getTime()),
				name: newItem.charAt(0).toUpperCase() + newItem.slice(1).toLowerCase(),
				type: newItem,
			};
		}
		onAdd(newItemType);
	};
	const handleDragEnd = (event) => {
		event.currentTarget.classList.remove(styles.dragging);
	};
	const handleRefresh = () => {
		setCount((prev) => prev + 1);
	};
	return (
		<div className={styles.toolbox}>
			<CustomTextField
				fullWidth
				className={`${styles.textField}`}
				InputProps={{
					endAdornment: (
						<InputAdornment position="end">
							{!searchValue && <IconComponent color="var(--grey-400)" icon={icon.faSearch} />}
							{searchValue && (
								<CustomIconButton
									className={styles.resetButton}
									icon={icon.faTimes}
									onClick={handleResetSearchText}
								/>
							)}
						</InputAdornment>
					),
					style: {
						fontSize: "var(--font-size-sm)",
						height: 32,
					},
				}}
				placeholder={translate("dsi.form-studio.view.creation.search-criteria")}
				size="small"
				value={searchValue}
				variant="outlined"
				onChange={handleChangeSearchText}
			/>
			<CustomButton
				fullWidth
				startIcon={icon.faPlus}
				variant="contained"
				onClick={() => setDialogType("creation")}
			>
				{translate("dsi.form-studio.view.creation.add-criteria")}
			</CustomButton>
			<div className={styles.criteriaContainer}>
				<div className={styles.toolCategoryTitle}>
					{translate("dsi.form-studio.view.creation.block.composition")}
				</div>
				<div
					draggable
					className="draggable-div" // Firefox hack
					// eslint-disable-next-line react/no-unknown-property
					unselectable="on"
					onDragEnd={handleDragEnd}
					onDragStart={(e) => handleDragStart(e, SECTION.SECTION)}
				>
					{translate("dsi.form-studio.view.creation.section")}
				</div>
				<div
					draggable
					className="draggable-div" // Firefox hack
					// eslint-disable-next-line react/no-unknown-property
					unselectable="on"
					onDragEnd={handleDragEnd}
					onDragStart={(e) => handleDragStart(e, SECTION.SUBSECTION)}
				>
					{translate("dsi.form-studio.view.creation.subsection")}
				</div>
				<div className={styles.toolCategoryTitle}>{translate("dsi.form-studio.view.creation.block.title")}</div>
				{isNonEmptyArray(criteriaList) &&
					criteriaList
						.filter((criteria) => !usedFields.includes(criteria.uuid))
						.map((criteria) => (
							<div
								key={criteria.id}
								draggable
								className={`draggable-div ${styles.criteria}`} // Firefox hack
								// eslint-disable-next-line react/no-unknown-property
								unselectable="on"
								onDragEnd={handleDragEnd}
								onDragStart={(e) => handleDragStart(e, criteria)}
							>
								<div>{criteria.name}</div>
								<div>
									<span className={styles.criteriaType}>
										<IconComponent
											color="var(--grey-400)"
											icon={icon[CRITERIA_TYPES[criteria?.type]?.icon]}
										/>
										<span>{translate(CRITERIA_TYPES[criteria?.type]?.name)}</span>
									</span>
								</div>
							</div>
						))}
			</div>
			{dialogType && (
				<CriteriaCreation
					dialogType={dialogType}
					open={!!dialogType}
					onClose={() => setDialogType("")}
					onRefresh={handleRefresh}
				/>
			)}
		</div>
	);
}
